import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import MainLayout from '../components/Layout/MainLayout';
import officeTransactionService from '../services/officeTransactionService';
import {
    BarChart,
    Bar,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    Cell
} from 'recharts';

const INITIAL_FILTERS = {
    fromDate: '',
    toDate: '',
    productName: '',
    officeLocation: ''
};

const INITIAL_FORM_DATA = {
    date: new Date().toISOString().split('T')[0],
    productName: '',
    quantity: '',
    amount: '',
    officeLocation: '',
    notes: ''
};

const currency = (value) => {
    const amount = Number(value || 0);
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 2
    }).format(amount);
};

const StatCard = ({ title, value, subtitle }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <p className="text-xs uppercase tracking-wide text-gray-500">{title}</p>
        <p className="text-2xl mt-2 font-semibold text-charcoal">{value}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
    </div>
);

const ChairmanOfficeTransactionPage = () => {
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [transactions, setTransactions] = useState([]);
    const [summary, setSummary] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 50 });
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [officeLocations, setOfficeLocations] = useState([]);
    const [productNames, setProductNames] = useState([]);

    const fetchData = async (page = 1) => {
        try {
            setLoading(true);
            const [transactionsResponse, summaryResponse, locationsResponse, productsResponse] = await Promise.all([
                officeTransactionService.getTransactions(filters, page, pagination.limit),
                officeTransactionService.getSummary(filters),
                officeTransactionService.getOfficeLocations(),
                officeTransactionService.getProductNames()
            ]);

            if (transactionsResponse.success) {
                setTransactions(transactionsResponse.data || []);
                setPagination(transactionsResponse.pagination || { page: 1, totalPages: 1, total: 0, limit: 50 });
            }

            if (summaryResponse.success) {
                setSummary(summaryResponse.data);
            }

            if (locationsResponse.success) {
                setOfficeLocations(locationsResponse.data || []);
            }

            if (productsResponse.success) {
                setProductNames(productsResponse.data || []);
            }
        } catch (error) {
            console.error('Fetch data error:', error);
            toast.error(error?.response?.data?.error || 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters(INITIAL_FILTERS);
    };

    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > pagination.totalPages) return;
        fetchData(newPage);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const submitData = {
                ...formData,
                quantity: Number(formData.quantity),
                amount: Number(formData.amount)
            };

            const response = await officeTransactionService.updateTransaction(editingId, submitData);

            if (response.success) {
                toast.success('Transaction updated successfully');
                setShowForm(false);
                setEditingId(null);
                setFormData(INITIAL_FORM_DATA);
                fetchData(pagination.page);
            } else {
                toast.error(response.error || 'Operation failed');
            }
        } catch (error) {
            console.error('Submit error:', error);
            toast.error(error?.response?.data?.error || 'Operation failed');
        }
    };

    const handleEdit = async (id) => {
        try {
            const response = await officeTransactionService.getTransactionById(id);
            if (response.success) {
                const transaction = response.data;
                setFormData({
                    date: new Date(transaction.date).toISOString().split('T')[0],
                    productName: transaction.productName,
                    quantity: transaction.quantity.toString(),
                    amount: transaction.amount.toString(),
                    officeLocation: transaction.officeLocation,
                    notes: transaction.notes || ''
                });
                setEditingId(id);
                setShowForm(true);
            }
        } catch (error) {
            console.error('Edit error:', error);
            toast.error('Failed to load transaction details');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this transaction?')) {
            return;
        }

        try {
            const response = await officeTransactionService.deleteTransaction(id);
            if (response.success) {
                toast.success('Transaction deleted successfully');
                fetchData(pagination.page);
            } else {
                toast.error('Failed to delete transaction');
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error(error?.response?.data?.error || 'Failed to delete transaction');
        }
    };

    // Chart data
    const locationChartData = useMemo(() => {
        if (!summary?.byLocation) return [];
        return summary.byLocation.map(item => ({
            name: item._id || 'Unknown',
            value: item.totalAmount,
            count: item.transactionCount
        }));
    }, [summary]);

    const monthlyChartData = useMemo(() => {
        if (!summary?.byMonth) return [];
        return summary.byMonth.slice(0, 12).map(item => ({
            month: `${item._id.year}-${String(item._id.month).padStart(2, '0')}`,
            amount: item.totalAmount,
            count: item.transactionCount
        })).reverse();
    }, [summary]);

    const colors = ['#3B82F6', '#22C55E', '#EF4444', '#F59E0B', '#8B5CF6', '#06B6D4', '#EC4899'];

    const serialStart = (pagination.page - 1) * pagination.limit;

    return (
        <MainLayout>
            <div className="max-w-7xl mx-auto p-4 space-y-6">
                <div>
                    <h1 className="text-3xl font-bold text-charcoal">Office Transaction Dashboard</h1>
                    <p className="text-sm text-gray-500 mt-1">Overview of office expenses and transactions</p>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                        <input
                            type="date"
                            name="fromDate"
                            value={filters.fromDate}
                            onChange={handleFilterChange}
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                            placeholder="From Date"
                        />
                        <input
                            type="date"
                            name="toDate"
                            value={filters.toDate}
                            onChange={handleFilterChange}
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                            placeholder="To Date"
                        />
                        <input
                            type="text"
                            name="productName"
                            value={filters.productName}
                            onChange={handleFilterChange}
                            placeholder="Product Name"
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                        />
                        <input
                            type="text"
                            name="officeLocation"
                            value={filters.officeLocation}
                            onChange={handleFilterChange}
                            placeholder="Office Location"
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                        />
                        <button
                            onClick={clearFilters}
                            className="px-4 py-2 bg-gray-100 text-charcoal rounded-lg text-sm hover:bg-gray-200"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>

                {/* Summary Cards */}
                {summary && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <StatCard
                            title="Total Amount"
                            value={currency(summary.overall.totalAmount)}
                            subtitle={`${summary.overall.totalTransactions} transactions`}
                        />
                        <StatCard
                            title="Average Transaction"
                            value={currency(summary.overall.avgAmount)}
                            subtitle="Per transaction"
                        />
                        <StatCard
                            title="Office Locations"
                            value={summary.byLocation?.length || 0}
                            subtitle="Active locations"
                        />
                    </div>
                )}

                {/* Charts */}
                {summary && (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                        {/* Location Distribution */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                            <h2 className="text-lg font-semibold text-charcoal mb-4">Expenses by Office Location</h2>
                            <div className="h-80">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={locationChartData}
                                            dataKey="value"
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={100}
                                            innerRadius={60}
                                            paddingAngle={2}
                                        >
                                            {locationChartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value) => currency(value)} />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Monthly Trend */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                            <h2 className="text-lg font-semibold text-charcoal mb-4">Monthly Expense Trend</h2>
                            <div className="h-80">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={monthlyChartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                                        <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`} />
                                        <Tooltip formatter={(value) => currency(value)} />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="amount"
                                            name="Amount"
                                            stroke="#3B82F6"
                                            strokeWidth={3}
                                            dot={{ r: 4 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                )}

                {/* Location Summary Bar Chart */}
                {summary && summary.byLocation && summary.byLocation.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                        <h2 className="text-lg font-semibold text-charcoal mb-4">Office Location Comparison</h2>
                        <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={locationChartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`} />
                                    <Tooltip formatter={(value) => currency(value)} />
                                    <Bar dataKey="value" fill="#1F2937" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                {/* Recent Transactions Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-4 border-b border-gray-100">
                        <h2 className="text-lg font-semibold text-charcoal">Recent Transactions</h2>
                    </div>
                    <div className="overflow-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-gray-600">
                                <tr>
                                    <th className="p-3 text-left">S.No</th>
                                    <th className="p-3 text-left">Date</th>
                                    <th className="p-3 text-left">Product Name</th>
                                    <th className="p-3 text-left">Quantity</th>
                                    <th className="p-3 text-left">Amount</th>
                                    <th className="p-3 text-left">Office Location</th>
                                    <th className="p-3 text-left">Created By</th>
                                    <th className="p-3 text-left">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={8} className="p-6 text-center text-gray-500">Loading transactions...</td></tr>
                                ) : transactions.length === 0 ? (
                                    <tr><td colSpan={8} className="p-6 text-center text-gray-500">No transactions found.</td></tr>
                                ) : (
                                    transactions.map((transaction, index) => (
                                        <tr key={transaction._id} className="border-t border-gray-100">
                                            <td className="p-3">{serialStart + index + 1}</td>
                                            <td className="p-3">{new Date(transaction.date).toLocaleDateString()}</td>
                                            <td className="p-3">{transaction.productName}</td>
                                            <td className="p-3">{transaction.quantity}</td>
                                            <td className="p-3 font-semibold">{currency(transaction.amount)}</td>
                                            <td className="p-3">{transaction.officeLocation}</td>
                                            <td className="p-3">{transaction.createdBy?.name || '-'}</td>
                                            <td className="p-3">
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleEdit(transaction._id)}
                                                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md text-xs hover:bg-blue-200"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(transaction._id)}
                                                        className="px-3 py-1 bg-red-100 text-red-700 rounded-md text-xs hover:bg-red-200"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between p-3 border-t border-gray-100">
                        <p className="text-sm text-gray-500">Total: {pagination.total || 0} records</p>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handlePageChange(pagination.page - 1)}
                                disabled={pagination.page <= 1}
                                className="px-3 py-1 rounded-md border border-gray-300 text-sm disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <span className="text-sm">Page {pagination.page} / {pagination.totalPages}</span>
                            <button
                                onClick={() => handlePageChange(pagination.page + 1)}
                                disabled={pagination.page >= pagination.totalPages}
                                className="px-3 py-1 rounded-md border border-gray-300 text-sm disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>

                {/* Edit Transaction Modal */}
                {showForm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
                            <h2 className="text-xl font-semibold text-charcoal">
                                Edit Transaction
                            </h2>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                                <input
                                    type="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleFormChange}
                                    required
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                                <input
                                    type="text"
                                    name="productName"
                                    value={formData.productName}
                                    onChange={handleFormChange}
                                    required
                                    list="product-names"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                    placeholder="Enter product name"
                                />
                                <datalist id="product-names">
                                    {productNames.map((name, idx) => (
                                        <option key={idx} value={name} />
                                    ))}
                                </datalist>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                                    <input
                                        type="number"
                                        name="quantity"
                                        value={formData.quantity}
                                        onChange={handleFormChange}
                                        required
                                        min="0"
                                        step="0.01"
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                        placeholder="0"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
                                    <input
                                        type="number"
                                        name="amount"
                                        value={formData.amount}
                                        onChange={handleFormChange}
                                        required
                                        min="0"
                                        step="0.01"
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Office Location</label>
                                <input
                                    type="text"
                                    name="officeLocation"
                                    value={formData.officeLocation}
                                    onChange={handleFormChange}
                                    required
                                    list="office-locations"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                    placeholder="Enter office location"
                                />
                                <datalist id="office-locations">
                                    {officeLocations.map((location, idx) => (
                                        <option key={idx} value={location} />
                                    ))}
                                </datalist>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                                <textarea
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleFormChange}
                                    rows={3}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                    placeholder="Additional notes..."
                                />
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowForm(false);
                                        setEditingId(null);
                                        setFormData(INITIAL_FORM_DATA);
                                    }}
                                    className="flex-1 px-4 py-2 bg-gray-100 text-charcoal rounded-lg hover:bg-gray-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    Update
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </MainLayout>
    );
};

export default ChairmanOfficeTransactionPage;